import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  ListGroup,
  Row,
  Spinner
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignLeft,
  faCheckSquare,
  faPlus,
  faTrashAlt,
  faUserCircle
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import BasicTemplate from 'components/Toast/BasicTemplate';
import Avatar from 'components/common/Avatar';
import UserDropdownV2 from 'components/dropdowns/CustomerServices/UserDropdownV2';
import { getHrUsers } from 'services/userServices';
import {
  getActionPlanSubTasks,
  createActionPlanSubTask,
  patchActionPlanSubTask,
  deleteActionPlanSubTask
} from 'services/customer-service/scActionPlanSubTaskService';

const NEW_SUB_TASK_ID = 'new';
const CLEAR_ASSIGNEE_ID = 0;
const emptyDraft = { title: '', description: '', assigneeId: null };

const singleLineTextStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0
};

const showError = message =>
  toast.error(
    <BasicTemplate
      success={false}
      text={message ?? 'Something went wrong. Please try again.'}
    />
  );

const getChangedSubTaskFields = (subTask, draft) => {
  const changedFields = {};
  const title = draft.title.trim();
  if (title !== subTask.title) changedFields.title = title;
  if (draft.description !== (subTask.description ?? '')) {
    changedFields.description = draft.description;
  }
  if ((draft.assigneeId ?? null) !== (subTask.assigneeId ?? null)) {
    changedFields.assigneeId = draft.assigneeId ?? CLEAR_ASSIGNEE_ID;
  }
  return changedFields;
};

const hasInitials = name => /\w/.test(name ?? '');

const ActionPlanSubTasks = ({ actionPlanId }) => {
  const [subTasks, setSubTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [isLoadingSubTasks, setIsLoadingSubTasks] = useState(true);
  const [editingSubTaskId, setEditingSubTaskId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [subTaskIdPendingDelete, setSubTaskIdPendingDelete] = useState(null);
  const [deletingSubTaskId, setDeletingSubTaskId] = useState(null);

  const isFormOpen = editingSubTaskId !== null;

  const loadSubTasks = async () => {
    setIsLoadingSubTasks(true);
    const result = await getActionPlanSubTasks(actionPlanId);
    if (result?.success) {
      setSubTasks(result.data ?? []);
    } else {
      showError(result?.message);
    }
    setIsLoadingSubTasks(false);
  };

  useEffect(() => {
    loadSubTasks();
  }, [actionPlanId]);

  useEffect(() => {
    let isMounted = true;
    getHrUsers().then(hrUsers => {
      if (isMounted && hrUsers?.length) setUsers(hrUsers);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const findUser = userId => users.find(user => user.value === userId);

  const openNewSubTaskForm = () => {
    setDraft(emptyDraft);
    setEditingSubTaskId(NEW_SUB_TASK_ID);
    setSubTaskIdPendingDelete(null);
  };

  const openEditSubTaskForm = subTask => {
    if (isFormOpen || deletingSubTaskId !== null) return;
    setDraft({
      title: subTask.title ?? '',
      description: subTask.description ?? '',
      assigneeId: subTask.assigneeId ?? null
    });
    setEditingSubTaskId(subTask.id);
    setSubTaskIdPendingDelete(null);
  };

  const closeSubTaskForm = () => {
    setEditingSubTaskId(null);
    setDraft(emptyDraft);
  };

  const handleDraftChange = (name, value) => {
    setDraft(current => ({ ...current, [name]: value }));
  };

  const saveNewSubTask = () => {
    const newSubTask = {
      title: draft.title.trim(),
      description: draft.description
    };
    if (draft.assigneeId) newSubTask.assigneeId = draft.assigneeId;
    return createActionPlanSubTask(actionPlanId, newSubTask);
  };

  const saveEditedSubTask = () => {
    const subTask = subTasks.find(x => x.id === editingSubTaskId);
    const changedFields = getChangedSubTaskFields(subTask, draft);
    if (Object.keys(changedFields).length === 0) return { success: true };
    return patchActionPlanSubTask(editingSubTaskId, changedFields);
  };

  const saveDraft = async () => {
    if (!draft.title.trim()) {
      showError('Sub task title is required.');
      return;
    }

    setIsSavingDraft(true);
    const result =
      editingSubTaskId === NEW_SUB_TASK_ID
        ? await saveNewSubTask()
        : await saveEditedSubTask();
    setIsSavingDraft(false);

    if (!result?.success) {
      showError(result?.message);
      return;
    }

    closeSubTaskForm();
    await loadSubTasks();
  };

  const deleteSubTask = async subTaskId => {
    setDeletingSubTaskId(subTaskId);
    const result = await deleteActionPlanSubTask(subTaskId);
    setDeletingSubTaskId(null);
    setSubTaskIdPendingDelete(null);

    if (!result?.success) {
      showError(result?.message);
      return;
    }

    setSubTasks(current => current.filter(x => x.id !== subTaskId));
  };

  const handleFormKeyDown = e => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!isSavingDraft) closeSubTaskForm();
    }
  };

  const handleTitleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveDraft();
    }
  };

  const renderAssignee = assigneeId => {
    const assignee = findUser(assigneeId);
    if (!assigneeId) {
      return (
        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
          <FontAwesomeIcon icon={faUserCircle} style={{ marginRight: '6px' }} />
          Unassigned
        </span>
      );
    }

    const assigneeName = assignee?.name ?? assignee?.label ?? '';
    return (
      <span
        className="d-flex align-items-center"
        style={{ fontSize: '0.8rem', minWidth: 0 }}
        title={assignee?.label}
      >
        {hasInitials(assigneeName) && (
          <Avatar size="m" name={assigneeName} className="me-2" />
        )}
        <span style={singleLineTextStyle}>{assigneeName}</span>
      </span>
    );
  };

  const renderSubTaskForm = ({ isNewSubTask }) => (
    <ListGroup.Item as="div" onKeyDown={handleFormKeyDown}>
      <Row className="gx-2 gy-2 align-items-center">
        <Col>
          <Form.Control
            size="sm"
            type="text"
            placeholder="What needs to be done?"
            value={draft.title}
            disabled={isSavingDraft}
            autoFocus
            onChange={e => handleDraftChange('title', e.target.value)}
            onKeyDown={handleTitleKeyDown}
          />
        </Col>
        <Col xs="12" md="4">
          <UserDropdownV2
            fieldName="assigneeId"
            placeholder="Assignee..."
            isMulti={false}
            isClearable
            disabled={isSavingDraft}
            value={draft.assigneeId}
            handleChange={(name, selectedUser) =>
              handleDraftChange(name, selectedUser?.value ?? null)
            }
          />
        </Col>
        <Col xs="auto">
          <Button
            size="sm"
            variant="primary"
            className="me-2"
            type="button"
            disabled={isSavingDraft}
            onClick={saveDraft}
          >
            {isSavingDraft ? 'Saving...' : isNewSubTask ? 'Create' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="falcon-default"
            type="button"
            disabled={isSavingDraft}
            onClick={closeSubTaskForm}
          >
            Cancel
          </Button>
        </Col>
        {!isNewSubTask && (
          <Col xs="12">
            <Form.Control
              as="textarea"
              size="sm"
              rows={3}
              placeholder="Description..."
              value={draft.description}
              disabled={isSavingDraft}
              onChange={e => handleDraftChange('description', e.target.value)}
            />
          </Col>
        )}
      </Row>
    </ListGroup.Item>
  );

  const renderDeleteControls = subTask => {
    const isDeleting = deletingSubTaskId === subTask.id;

    if (subTaskIdPendingDelete !== subTask.id) {
      return (
        <Button
          size="sm"
          variant="link"
          type="button"
          className="text-muted p-0 ms-3"
          title="Delete sub task"
          disabled={isFormOpen}
          onClick={() => setSubTaskIdPendingDelete(subTask.id)}
        >
          <FontAwesomeIcon icon={faTrashAlt} />
        </Button>
      );
    }

    return (
      <span className="ms-3 d-flex align-items-center">
        <Button
          size="sm"
          variant="danger"
          className="me-2"
          type="button"
          disabled={isDeleting}
          onClick={() => deleteSubTask(subTask.id)}
        >
          {isDeleting ? 'Deleting...' : 'Delete'}
        </Button>
        <Button
          size="sm"
          variant="falcon-default"
          type="button"
          disabled={isDeleting}
          onClick={() => setSubTaskIdPendingDelete(null)}
        >
          Cancel
        </Button>
      </span>
    );
  };

  const renderSubTaskRow = subTask => (
    <ListGroup.Item
      key={subTask.id}
      as="div"
      action
      className="d-flex align-items-center py-2"
      style={{ cursor: 'pointer', userSelect: 'none' }}
      title="Double-click to edit"
      onDoubleClick={() => openEditSubTaskForm(subTask)}
    >
      <FontAwesomeIcon
        icon={faCheckSquare}
        className="text-primary"
        style={{ marginRight: '10px' }}
      />
      <span style={{ ...singleLineTextStyle, flex: 1 }}>{subTask.title}</span>
      {subTask.description && (
        <FontAwesomeIcon
          icon={faAlignLeft}
          className="text-muted"
          title={subTask.description}
          style={{ marginLeft: '8px' }}
        />
      )}
      <span
        className="d-flex justify-content-end"
        style={{ width: '200px', marginLeft: '16px', minWidth: 0 }}
      >
        {renderAssignee(subTask.assigneeId)}
      </span>
      <span onDoubleClick={e => e.stopPropagation()}>
        {renderDeleteControls(subTask)}
      </span>
    </ListGroup.Item>
  );

  return (
    <Card className="mb-3">
      <Card.Header>
        <Row className="flex-between-center">
          <Col>
            <h5 className="mb-0 d-flex align-items-center">
              Sub Tasks
              {subTasks.length > 0 && (
                <Badge bg="secondary" pill style={{ marginLeft: '8px' }}>
                  {subTasks.length}
                </Badge>
              )}
              {isLoadingSubTasks && (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  style={{ marginLeft: '8px' }}
                />
              )}
            </h5>
          </Col>
          <Col xs="auto">
            <Button
              size="sm"
              variant="falcon-default"
              type="button"
              disabled={isFormOpen}
              onClick={openNewSubTaskForm}
            >
              <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
              Add sub task
            </Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body className="bg-light">
        {!isLoadingSubTasks && subTasks.length === 0 && !isFormOpen && (
          <div className="text-muted">No sub tasks yet.</div>
        )}
        {(subTasks.length > 0 || isFormOpen) && (
          <ListGroup>
            {subTasks.map(subTask =>
              subTask.id === editingSubTaskId ? (
                <React.Fragment key={subTask.id}>
                  {renderSubTaskForm({ isNewSubTask: false })}
                </React.Fragment>
              ) : (
                renderSubTaskRow(subTask)
              )
            )}
            {editingSubTaskId === NEW_SUB_TASK_ID &&
              renderSubTaskForm({ isNewSubTask: true })}
          </ListGroup>
        )}
        {subTasks.length > 0 && !isFormOpen && (
          <div className="text-muted" style={{ fontSize: '0.75rem', marginTop: '6px' }}>
            Double-click a sub task to edit.
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

ActionPlanSubTasks.propTypes = {
  actionPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired
};

export default ActionPlanSubTasks;
